import { NAV_ITEMS } from '@mts/utils/navItems';
import { formatPhoneNumber } from '../utils/phone.js';


const UTILITY_LINKS = [
  { label: 'Spotlight Blog', href: '/blog' },
  { label: 'Log In', href: '/login' },
  { label: 'Register', href: '/register' },
];

const FOOTER_SECTIONS = [
  {
    title: 'For Businesses',
    links: [
      { label: 'Claim Your Listing', href: '/claim/new' },
      { label: 'Advertise With Us', href: '/advertise/spotlight' },
      { label: 'Business Directory', href: '/listings' },
      { label: 'Business Resources', href: '/blog' },
    ],
  },
  {
    title: 'Community',
    links: [
      { label: 'Events Calendar', href: '/events' },
      { label: 'Deals', href: '/offers' },
      { label: 'Blog & Local News', href: '/blog' },
      { label: 'Local Guide', href: '/listings' },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact Us', href: '/contact' },
    ],
  },
];

const DEFAULT_WEBSITE_NAVIGATION = [
  { label: 'Home', href: '/', location: 'both', sortOrder: 10 },
  { label: 'Businesses', href: '/listings', location: 'both', sortOrder: 20 },
  { label: 'Deals', href: '/offers', location: 'both', sortOrder: 30 },
  {
    label: 'Advertise With Us!',
    href: '/advertise/spotlight',
    location: 'both',
    sortOrder: 35,
    children: [
      { label: 'Spotlight Online', href: '/advertise/spotlight' },
      { label: 'Monthly Mailer', href: '/advertise/mailer' },
    ],
  },
  { label: 'Events', href: '/events', location: 'both', sortOrder: 40 },
  { label: 'About', href: '/about', location: 'both', sortOrder: 50 },
  { label: 'Contact', href: '/contact', location: 'both', sortOrder: 60 },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Use', href: '/terms-of-use' },
  { label: 'Cookie Policy', href: '/cookie-policy' },
];

export function canonicalPublicHref(href = '') {
  const value = String(href || '').trim();
  if (!value.startsWith('/')) return value;
  const match = value.match(/^([^?#]*)([?#].*)?$/);
  const aliases = { '/directory': '/listings', '/deals': '/offers', '/articles': '/blog', '/pricing': '/advertise/spotlight', '/home': '/' };
  const path = match?.[1] || value;
  const aliasKey = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return `${aliases[aliasKey] || path}${match?.[2] || ''}`;
}

export function publicPhoneHref(value = '') {
  const original = String(value || '').trim();
  const digits = original.replace(/\D/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  const normalized = original.replace(/[^+\d]/g, '');
  return normalized ? `tel:${normalized}` : '';
}

function normalizeNavigationItems(items, location) {
  const source = Array.isArray(items) && items.length > 0 ? items : DEFAULT_WEBSITE_NAVIGATION;
  return source
    .filter((item) => item?.location === location || item?.location === 'both')
    .map((item) => ({
      label: String(item?.label || '').trim(),
      href: canonicalPublicHref(item?.href),
      sortOrder: Number(item?.sortOrder || 0),
      children: Array.isArray(item?.children)
        ? item.children
            .map((child) => ({
              label: String(child?.label || '').trim(),
              href: canonicalPublicHref(child?.href),
            }))
            .filter((child) => child.label && child.href)
        : [],
    }))
    .filter((item) => item.label && item.href)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}

function isActiveNavItem(item, pathname) {
  if (!item?.href) return false;
  if (Array.isArray(item.children) && item.children.some((child) => isActiveNavItem(child, pathname))) return true;
  if (item.href === '/') return pathname === '/';
  const pathOnly = String(item.href).split('#')[0].split('?')[0];
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
}

export function buildPublicHeaderConfig({
  pathname,
  hostname,
  spotlightName,
  logo,
  contactEmail,
  contactPhone,
  utilityLinks,
  navigationItems,
  isMobile = false,
}) {
  const headerNavItems = normalizeNavigationItems(navigationItems, 'header');
  return {
    showTopBar: !isMobile,
    brand: {
      title: spotlightName,
      subtitle: hostname,
      href: '/',
      logo: logo || null,
    },
    support: {
      email: contactEmail,
      phone: formatPhoneNumber(contactPhone),
      phoneHref: publicPhoneHref(contactPhone),
    },
    utilityLinks: (utilityLinks || UTILITY_LINKS).map((item) => ({ ...item, href: canonicalPublicHref(item?.href) })),
    navItems: (headerNavItems.length > 0 ? headerNavItems : NAV_ITEMS).map((item) => ({
      label: item.label,
      href: item.href,
      children: item.children || [],
      active: typeof item.matches === 'function' ? item.matches(pathname) : isActiveNavItem(item, pathname),
    })),
    cta: {
      label: 'Claim Your Listing',
      href: '/claim/new',
    },
  };
}

export function buildPublicFooterConfig({ spotlightName, logo, socialContent, contactInfo, navigationItems }) {
  const contactLinks = [];
  if (contactInfo?.email) {
    contactLinks.push({ label: contactInfo.email, href: `mailto:${contactInfo.email}` });
  }
  if (contactInfo?.phone) {
    contactLinks.push({ label: formatPhoneNumber(contactInfo.phone), href: publicPhoneHref(contactInfo.phone) });
  }
  if (contactInfo?.address) {
    contactLinks.push({ label: contactInfo.address, href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}` });
  }

  const footerNavigation = normalizeNavigationItems(navigationItems, 'footer');
  const navigationSection = footerNavigation.length > 0
    ? [{ title: 'Navigation', links: footerNavigation.map(({ label, href }) => ({ label, href })) }]
    : [];

  const sections = contactLinks.length > 0
    ? [
        ...navigationSection,
        ...FOOTER_SECTIONS,
        { title: 'Contact', links: contactLinks },
      ]
    : [
        ...navigationSection,
        ...FOOTER_SECTIONS,
      ];

  return {
    brandName: spotlightName,
    brandLogo: logo || null,
    brandDescription: 'Connecting our community with local businesses, events, and experiences.',
    socialContent,
    sections,
    legalLinks: LEGAL_LINKS,
    copyright: `© ${new Date().getFullYear()} ${spotlightName}. All rights reserved.`,
  };
}
